/**
 * 基础 UI 组件（Optical Tactical System）。
 *
 * 视觉约定：
 * - 所有容器 = 75% 光学透镜 + 135° 镜面高光，不使用实线描边；
 * - 阵营色只作为点光源/微光，不铺满背景；
 * - 标题用冷白金属渐变，元数据用枪灰 mono micro-label。
 */
import type { ReactNode } from 'react';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function Panel({
  children,
  className,
  title,
  subtitle,
  actions,
  accent,
  testId,
}: {
  children?: ReactNode;
  className?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  accent?: 'AFF' | 'NEG' | 'BLIND' | 'NEUTRAL';
  testId?: string;
}) {
  const accentClass =
    accent === 'AFF'
      ? 'text-team-aff'
      : accent === 'NEG'
        ? 'text-team-neg'
        : accent === 'BLIND'
          ? 'text-double-blind'
          : 'text-slate-300';
  const dotClass =
    accent === 'AFF'
      ? 'status-dot status-dot--aff'
      : accent === 'NEG'
        ? 'status-dot status-dot--neg'
        : accent === 'BLIND'
          ? 'status-dot'
          : 'status-dot';
  return (
    <section
      data-testid={testId}
      data-accent={accent ?? 'NEUTRAL'}
      className={cx('lens spotlight-card', className)}
    >
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={cx('h-1.5 w-1.5 rounded-full', accentClass)} style={{ background: 'currentColor', boxShadow: '0 0 10px currentColor' }} />
              {typeof title === 'string' ? (
                <h2 className="truncate text-[13px] font-medium tracking-wide text-slate-100">{title}</h2>
              ) : (
                title
              )}
            </div>
            {subtitle && <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      {(title || actions) && <div className="hairline mx-5" />}
      <div className={cx('px-5', title || actions ? 'py-4' : 'py-5')}>{children}</div>
    </section>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'aff' | 'neg' | 'blind' | 'pass' | 'violation' | 'warning' | 'neutral';
  className?: string;
}) {
  const tones: Record<string, string> = {
    aff: 'text-[#BAE6FD]',
    neg: 'text-[#FECDD3]',
    blind: 'text-[#C7D2FE]',
    pass: 'text-[#BBF7D0]',
    violation: 'text-[#FECDD3]',
    warning: 'text-[#FDE68A]',
    neutral: 'text-slate-300',
  };
  const dot: Record<string, string> = {
    aff: 'bg-[#BAE6FD] shadow-[0_0_8px_rgba(186,230,253,0.7)]',
    neg: 'bg-[#FDA4AF] shadow-[0_0_8px_rgba(253,164,175,0.65)]',
    blind: 'bg-[#A5B4FC] shadow-[0_0_8px_rgba(165,180,252,0.6)]',
    pass: 'bg-[#86EFAC] shadow-[0_0_8px_rgba(134,239,172,0.6)]',
    violation: 'bg-[#FB7185] shadow-[0_0_8px_rgba(251,113,133,0.7)]',
    warning: 'bg-[#FCD34D] shadow-[0_0_8px_rgba(252,211,77,0.65)]',
    neutral: 'bg-slate-500',
  };
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-white/[0.035] px-2 py-[3px] font-mono text-[10px] leading-none tracking-wide shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(255,255,255,0.04)]',
        tones[tone],
        className,
      )}
    >
      <span className={cx('h-1 w-1 rounded-full', dot[tone])} />
      {children}
    </span>
  );
}

export function Button({
  children,
  onClick,
  variant = 'secondary',
  size = 'md',
  disabled,
  title,
  testId,
  className,
  hotkey,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  title?: string;
  testId?: string;
  className?: string;
  hotkey?: string;
  type?: 'button' | 'submit';
}) {
  const variants: Record<string, string> = {
    primary:
      'bg-[#F8FAFC] text-[#050507] hover:bg-white shadow-[0_10px_30px_-10px_rgba(255,255,255,0.35)]',
    secondary:
      'bg-white/[0.045] text-slate-100 hover:bg-white/[0.09] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),inset_0_0_0_1px_rgba(255,255,255,0.05)]',
    ghost: 'bg-transparent text-slate-400 hover:bg-white/[0.05] hover:text-slate-100',
    danger:
      'bg-[#FB7185]/10 text-[#FECDD3] hover:bg-[#FB7185]/16 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(251,113,133,0.22)]',
    success:
      'bg-[#86EFAC]/10 text-[#DCFCE7] hover:bg-[#86EFAC]/16 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(134,239,172,0.22)]',
  };
  const sizes: Record<string, string> = {
    sm: 'px-2.5 py-1 text-[11px]',
    md: 'px-3.5 py-1.5 text-[12px]',
    lg: 'px-5 py-2.5 text-[14px]',
  };
  return (
    <button
      type={type}
      data-testid={testId}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-35',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {children}
      {hotkey && (
        <kbd className="rounded bg-black/25 px-1 py-0.5 font-mono text-[9px] text-current opacity-70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]">
          {hotkey}
        </kbd>
      )}
    </button>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'aff' | 'neg' | 'neutral';
  testId?: string;
}) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="micro-label">{label}</div>
      <div
        className={cx(
          'mono mt-1 truncate text-xl font-semibold tracking-tight',
          tone === 'aff' ? 'text-team-aff' : tone === 'neg' ? 'text-team-neg' : 'text-slate-100',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="lens-inset flex flex-col items-center justify-center px-4 py-7 text-center">
      <div className="text-[12px] text-slate-300">{title}</div>
      {detail && <div className="mt-1 max-w-md text-[11px] leading-relaxed text-slate-500">{detail}</div>}
    </div>
  );
}

export function KeyHint({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-[10px] text-slate-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(255,255,255,0.05)]">
      {children}
    </kbd>
  );
}
